import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, Github, Eye, Star, Calendar, Code2 } from 'lucide-react';
import './ProjectCard.css';

const ProjectCard = ({ 
  title, 
  category, 
  image, 
  description, 
  liveUrl, 
  githubUrl, 
  technologies = [], 
  featured = false,
  viewMode = 'grid' 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const cardVariants = {
    rest: { scale: 1, y: 0 },
    hover: { 
      scale: featured ? 1.02 : 1.05, 
      y: -8,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  const overlayVariants = {
    rest: { opacity: 0, scale: 0.8 },
    hover: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  const imageVariants = {
    rest: { scale: 1 },
    hover: { 
      scale: 1.1,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  if (viewMode === 'list') {
    return (
      <motion.div 
        className={`project-card list-card ${featured ? 'featured' : ''}`}
        variants={cardVariants}
        initial="rest"
        whileHover="hover"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
      >
        <div className="list-card-content">
          <div className="list-image">
            {image ? (
              <motion.img 
                src={image} 
                alt={title} 
                className="project-img"
                variants={imageVariants}
                onLoad={() => setImageLoaded(true)}
              />
            ) : (
              <div className="project-placeholder">
                <span>{title[0]}</span>
              </div>
            )}
            <motion.div 
              className="list-overlay"
              variants={overlayVariants}
            >
              <div className="project-links">
                {liveUrl && (
                  <motion.a 
                    href={liveUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="project-link"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ExternalLink size={18} />
                  </motion.a>
                )}
                {githubUrl && (
                  <motion.a 
                    href={githubUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="project-link"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Github size={18} />
                  </motion.a>
                )}
              </div>
            </motion.div>
          </div>
          
          <div className="list-info">
            <div className="list-header">
              <div className="project-meta">
                <span className="project-category">{category}</span>
                {featured && <Star className="featured-icon" size={16} />}
              </div>
              <h3 className="project-title">{title}</h3>
              <p className="project-description">{description}</p>
            </div>
            
            {technologies.length > 0 && (
              <div className="project-technologies">
                {technologies.slice(0, 4).map((tech, index) => (
                  <span key={index} className="tech-tag">
                    {tech}
                  </span>
                ))}
                {technologies.length > 4 && (
                  <span className="tech-more">+{technologies.length - 4}</span>
                )}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className={`project-card grid-card ${featured ? 'featured' : ''}`}
      variants={cardVariants}
      initial="rest"
      whileHover="hover"
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <div className="project-card-inner">
        <div className="project-image">
          {image ? (
            <motion.img 
              src={image} 
              alt={title} 
              className="project-img"
              variants={imageVariants}
              onLoad={() => setImageLoaded(true)}
            />
          ) : (
            <div className="project-placeholder">
              <motion.span
                animate={{ rotate: isHovered ? 360 : 0 }}
                transition={{ duration: 0.6 }}
              >
                {title[0]}
              </motion.span>
              <div className="placeholder-bg">
                <Code2 size={40} className="placeholder-icon" />
              </div>
            </div>
          )}
          
          <motion.div 
            className="project-overlay"
            variants={overlayVariants}
          >
            <div className="overlay-content">
              <div className="project-links">
                {liveUrl && (
                  <motion.a 
                    href={liveUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="project-link live-link"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <ExternalLink size={20} />
                    <span>Live Demo</span>
                  </motion.a>
                )}
                {githubUrl && (
                  <motion.a 
                    href={githubUrl} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="project-link code-link"
                    whileHover={{ scale: 1.1, rotate: -5 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Github size={20} />
                    <span>View Code</span>
                  </motion.a>
                )}
              </div>
              
              {!liveUrl && !githubUrl && (
                <div className="coming-soon">
                  <Eye size={24} />
                  <span>Coming Soon</span>
                </div>
              )}
            </div>
          </motion.div>

          {featured && (
            <div className="featured-badge">
              <Star size={14} />
              <span>Featured</span>
            </div>
          )}
        </div>
        
        <div className="project-info">
          <div className="project-header">
            <span className="project-category">{category}</span>
            <div className="project-status">
              <div className="status-indicator"></div>
              <span>Active</span>
            </div>
          </div>
          
          <h3 className="project-title">{title}</h3>
          <p className="project-description">{description}</p>
          
          {technologies.length > 0 && (
            <div className="project-technologies">
              <div className="tech-header">
                <Code2 size={16} />
                <span>Tech Stack</span>
              </div>
              <div className="tech-list">
                {technologies.slice(0, 3).map((tech, index) => (
                  <motion.span 
                    key={index} 
                    className="tech-tag"
                    whileHover={{ scale: 1.05 }}
                  >
                    {tech}
                  </motion.span>
                ))}
                {technologies.length > 3 && (
                  <span className="tech-more">+{technologies.length - 3}</span>
                )}
              </div>
            </div>
          )}

          <div className="project-footer">
            <div className="project-actions">
              {liveUrl && (
                <motion.a 
                  href={liveUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="action-btn primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ExternalLink size={16} />
                  Live Demo
                </motion.a>
              )}
              {githubUrl && (
                <motion.a 
                  href={githubUrl} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="action-btn secondary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Github size={16} />
                  Code
                </motion.a>
              )}
            </div>
          </div>
        </div>

        <div className="project-glow"></div>
      </div>
    </motion.div>
  );
};

export default ProjectCard;